import React, { useState } from 'react';
import type { WidgetManifest } from '../../services/myToolsApi';
import WidgetPermissionList from './WidgetPermissionList';
import { widgetNeedsAcknowledgement, widgetAcknowledgementText } from './widgetPermissions';
import { applyInstallControls, grantedFromSelection, DEFAULT_CONTROLS, CONTROL_KEYS } from './widgetInstallControls';

/**
 * The same permission-acknowledgement + connection-secret consent screen
 * MyToolsPage's MarketplaceInstallModal already shows for a widget install, extracted so
 * MarketplaceSidebarPanel (Toolbox) can show the identical screen before a Toolbox-triggered
 * install — installing a widget from Toolbox has no admin gate, but the installer still gets
 * the same plain-language permission consent every other widget install already requires.
 * Toolbox installs are always agency-wide (see marketplace_widget_bridge_migration.sql), so
 * this only ever renders the "shared" half of what MarketplaceInstallModal shows — no per-user
 * config/connection fields, and no MCP header fields (Toolbox never lists an MCP server).
 */
const WidgetInstallConsent: React.FC<{
  manifest: WidgetManifest | null | undefined;
  toolName: string;
  onCancel: () => void;
  onConfirm: (config: Record<string, string>, connectionValues?: Record<string, string>) => void;
  installing: boolean;
  /** Why the last install attempt failed; the screen stays open so it can be fixed and retried. */
  error?: string | null;
}> = ({ manifest, toolName, onCancel, onConfirm, installing, error }) => {
  const connectionFields = (manifest?.connections ?? []).filter(c => c.scope !== 'per_user');
  const [connectionValues, setConnectionValues] = useState<Record<string, string>>(
    () => Object.fromEntries(connectionFields.map(c => [c.key, ''])),
  );
  const [trusted, setTrusted] = useState(false);
  const manifestPermissions = manifest?.permissions ?? [];
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(() => new Set(manifestPermissions));
  const effectiveManifest = applyInstallControls(manifest, { ...DEFAULT_CONTROLS, grantedPermissions: grantedFromSelection(manifest, selectedPermissions) });
  const requiresAck = widgetNeedsAcknowledgement(effectiveManifest);
  const [ackAccepted, setAckAccepted] = useState(false);
  const canSubmit = connectionFields.every(c => !c.required || connectionValues[c.key]?.trim())
    && trusted && (!requiresAck || ackAccepted);

  const handleConfirm = () => {
    const granted = grantedFromSelection(manifest, selectedPermissions);
    const config: Record<string, string> = {
      [CONTROL_KEYS.permissions]: JSON.stringify(granted),
      [CONTROL_KEYS.decided]: JSON.stringify(manifestPermissions),
    };
    onConfirm(config, connectionFields.length > 0 ? connectionValues : undefined);
  };

  return (<div className="mt-modal-overlay" onClick={e => { if (e.target === e.currentTarget && !installing) onCancel(); }}>
    <div className="mt-modal">
      <div className="mt-modal__header">
        <h3 className="mt-modal__title">Install {toolName} for your agency</h3>
        <button type="button" className="mt-modal__close" onClick={onCancel} disabled={installing} aria-label="Close">×</button>
      </div>
      <div style={{ margin: '4px 0 12px' }}>
        <span className="wg-perm-title">This widget will be able to (for everyone in your agency)</span>
        <WidgetPermissionList
          manifest={manifest}
          emptyText="Nothing beyond drawing its own UI — no agency data, no network, no access to Momentum AMP"
          selectable={manifestPermissions.length > 0}
          selected={selectedPermissions}
          onToggle={(key, on) => setSelectedPermissions(prev => {
            const next = new Set(prev);
            if (on) next.add(key); else next.delete(key);
            return next;
          })}
        />
        {manifestPermissions.length > 0 && (
          <p className="wg-controls__hint" style={{ marginTop: 6 }}>
            Untick anything you don't want to grant — the widget still installs, that capability is
            simply refused when it asks. Everyone in your agency can change this later under
            Installed → Configuration.
          </p>
        )}
        {requiresAck && (<label className="wg-perm-ack">
          <input type="checkbox" checked={ackAccepted} onChange={e => setAckAccepted(e.target.checked)}/>
          <span>{widgetAcknowledgementText(effectiveManifest, true)}</span>
        </label>)}
      </div>
      <div className="mt-form">
        {connectionFields.length > 0 && (<>
          <p className="mt-step-subtitle">
            Connect to other services (shared with your whole agency) — Momentum AMP holds these
            keys for the widget; it never sees them itself.
          </p>
          {connectionFields.map(c => (<div key={c.key} className="mt-field mt-field--full">
            <label className="mt-label">{c.label} {c.required && <span className="mt-required">*</span>}</label>
            {c.hint && <p className="mt-field-hint">{c.hint}</p>}
            <input className="mt-input" type="password" autoComplete="off" value={connectionValues[c.key] ?? ''} onChange={e => setConnectionValues(v => ({ ...v, [c.key]: e.target.value }))}/>
          </div>))}
        </>)}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={trusted} onChange={e => setTrusted(e.target.checked)} style={{ marginTop: 2 }}/>
          I trust this community shared item and understand it comes from another agency in the Momentum AMP community, not from Momentum AMP directly.
        </label>
      </div>
      {error && !installing && (<p role="alert" style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--accent-red, #ef4444)' }}>
        Couldn't install: {error}
      </p>)}
      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary" onClick={onCancel} disabled={installing}>Cancel</button>
        <button type="button" className="mt-btn mt-btn--primary" disabled={!canSubmit || installing} onClick={handleConfirm}>
          {installing ? 'Installing…' : 'Install'}
        </button>
      </div>
    </div>
  </div>);
};

export default WidgetInstallConsent;
